import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../util/themes";
import {
    Drawer,
    Box,
    Typography,
    TextField,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import { DrawerHeader } from "./ShoppingCartDrawerHeader";
import { EmptyCart } from "./ShoppingCartEmptyCart";
import { STYLES } from "./ShoppingCartStyles";
import { SummarySection } from "./SummarySection";
import { ShoppingCartItem } from "./ShoppingCartItem";
import { useCart } from "@ecommerce/store";
import { HttpOrder, HttpShippings } from "../../http";
import type { ShippingCalculationRequest } from "../../http/httpShippings";
import {
    OrderCalculationRequest,
    OrderCalculationRequestFields,
    OrderProduct,
    OrderProductFields,
    OrderCalculationView,
    OrderCalculationViewFields,
    OrderShippingViewDTO,
    OrderShippingViewDTOFields,
} from "../../types/purchaseOrder/Order";
import { BaseResponseFields, EntityWithIdFields } from "../../types/baseEntities";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";
import { useShipping } from "@ecommerce/checkout";
import { mapsUrlBuilder } from "@ecommerce/shared-utils";
import { SHIPPING_ACTION_LINK_SX } from "../../styles/shippingActionLinkStyles";
import { BranchPickerDialog } from "../Shipping/BranchPickerDialog";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { useSnackbar } from "notistack";
import { getMinimumPurchaseQuantity } from "../../utils/purchaseRules";

interface ShoppingCartDrawerProps {
    cartItems: any[];
    isOpen: boolean;
    onClose: () => void;
    cartCount: number;
}

type Branch = {
    id?: string;
    nombre?: string;
    direccion?: string;
    codigoPostal?: string;
    latitud?: string;
    longitud?: string;
    [key: string]: any;
};

const makeShippingValue = (shippingOptionId: string, branchId: string | null) =>
    `${shippingOptionId}::${branchId ?? ""}`;

const parseShippingValue = (value: string) => {
    const [shippingOptionId, branchIdRaw] = value.split("::");
    const branchId = branchIdRaw ? branchIdRaw : null;
    return { shippingOptionId, branchId };
};

const ShoppingCartDrawer: React.FC<ShoppingCartDrawerProps> = ({
    cartItems,
    isOpen,
    onClose,
    cartCount,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const minimumPurchase = getMinimumPurchaseQuantity();
    const { updateQuantity, removeFromCart } = useCart();
    const { enqueueSnackbar } = useSnackbar();
    const {
        zipCode: contextZipCode,
        setZipCode: setContextZipCode,
        selectedShippingOptionId,
        setSelectedShippingOptionId,
        selectedShippingOptionBranchId,
        setSelectedShippingOptionBranchId,
    } = useShipping();

    const [localZipCode, setLocalZipCode] = useState(contextZipCode || "");
    const [showShippingOptions, setShowShippingOptions] = useState(false);
    const [shippingOptions, setShippingOptions] = useState<OrderShippingViewDTO[]>([]);
    const [calculationData, setCalculationData] = useState<OrderCalculationView | null>(null);
    const [isLoadingCalculation, setIsLoadingCalculation] = useState(false);
    const [isLoadingShippingOptions, setIsLoadingShippingOptions] = useState(false);
    const [pendingCartLines, setPendingCartLines] = useState<Set<string>>(new Set());
    const pendingCartLinesRef = useRef<Set<string>>(new Set());

    const [branchDialogOpen, setBranchDialogOpen] = useState(false);
    const [branchDialogOptionId, setBranchDialogOptionId] = useState<string | null>(null);

    const openBranchDialog = (optionId: string) => {
        setBranchDialogOptionId(optionId);
        setBranchDialogOpen(true);
    };

    const closeBranchDialog = () => {
        setBranchDialogOpen(false);
        setBranchDialogOptionId(null);
    };

    const getOptionById = (optionId: string) =>
        shippingOptions.find((o) => o[EntityWithIdFields.Id] === optionId);

    const getBranchesForOption = (optionId: string): Branch[] => {
        const opt = getOptionById(optionId);
        return (opt?.[OrderShippingViewDTOFields.Branches] ?? []) as Branch[];
    };

    const applySelection = (optionId: string, branchId: string | null) => {
        setSelectedShippingOptionId(optionId);
        setSelectedShippingOptionBranchId(branchId);
        calculateOrder(optionId);
    };

    const dialogOption = branchDialogOptionId ? getOptionById(branchDialogOptionId) : null;
    const dialogBranches = branchDialogOptionId ? getBranchesForOption(branchDialogOptionId) : [];

    const getAdaptiveColor = (
        colorType: "primary" | "secondary" | "border" | "background" | "success" | "radio",
    ) => {
        switch (colorType) {
            case "primary":
                return theme.colors.text.primary;
            case "secondary":
                return theme.colors.text.secondary;
            case "border":
                return theme.colors.greyLight;
            case "background":
                return theme.colors.background.paper;
            case "success":
                return theme.colors.success;
            case "radio":
                return theme.colors.greyMedium;
            default:
                return theme.colors.text.primary;
        }
    };

    const selectedShippingOption = useMemo(() => {
        return shippingOptions.find((opt) => opt.id === selectedShippingOptionId);
    }, [shippingOptions, selectedShippingOptionId]);

    const isShippingToBeAgreed = !!(
        selectedShippingOption &&
        !selectedShippingOption[OrderShippingViewDTOFields.FixedCost] &&
        !selectedShippingOption[OrderShippingViewDTOFields.IntegratedCost]
    );

    const fetchShippingOptions = async (zipCode: string) => {
        setIsLoadingShippingOptions(true);
        setShippingOptions([]);
        setSelectedShippingOptionId(null);
        setSelectedShippingOptionBranchId(null);
        setBranchDialogOpen(false);
        setBranchDialogOptionId(null);

        calculateOrder(null);

        try {
            // construyo el DTO para el backend
            const requestData: ShippingCalculationRequest = {
                codigoPostal: zipCode,
                productosOrden: cartItems.map((item) => ({
                    idProducto: item.idProduct,
                    // ACA TENGO QUE MANDAR IDVARIANTE
                    idProductoVariante: item.idProductVariant,
                    cantidad: item.quantity,
                })),
            };

            // nuevo endpoint para calcular envios
            const options = await HttpShippings.calculateShippingOptions(requestData);
            setShippingOptions(options);

            if (options.length > 0) {
                setShowShippingOptions(true);
            }
        } catch (error) {
            console.error("Error calculating shipping options:", error);
        } finally {
            setIsLoadingShippingOptions(false);
        }
    };

    // Restore zip code and show options if previously set
    useEffect(() => {
        if (contextZipCode) {
            setLocalZipCode(contextZipCode);
            setShowShippingOptions(true);
        }
    }, [contextZipCode]);

    const { subtotal, total } = useMemo(() => {
        if (calculationData) {
            return {
                subtotal: calculationData[OrderCalculationViewFields.Subtotal],
                total: calculationData[OrderCalculationViewFields.Total],
            };
        }

        const sub = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
        return {
            subtotal: sub,
            total: sub,
        };
    }, [cartItems, calculationData]);

    const { productsDiscount, shippingDiscount, shippingOriginalCost, finalShippingCost } =
        useMemo(() => {
            if (calculationData) {
                const shippingCostValue = calculationData[OrderCalculationViewFields.ShippingCost];
                const shippingDiscountValue =
                    calculationData[OrderCalculationViewFields.ShippingDiscount];
                return {
                    productsDiscount: calculationData[OrderCalculationViewFields.ProductsDiscount],
                    shippingDiscount: shippingDiscountValue,
                    shippingOriginalCost: shippingCostValue,
                    finalShippingCost: shippingCostValue - shippingDiscountValue,
                };
            }
            return {
                productsDiscount: 0,
                shippingDiscount: 0,
                shippingOriginalCost: undefined,
                finalShippingCost: null,
            };
        }, [calculationData]);

    const calculateOrder = async (shippingOptionId: string | null = null) => {
        if (cartItems.length === 0) {
            setCalculationData(null);
            return;
        }

        setIsLoadingCalculation(true);

        let selectedShippingCost: number | null = null;
        if (shippingOptionId) {
            // Buscamos en el estado 'shippingOptions' que ya tenemos cargado con los precios de MiCorreo
            const selectedOption = shippingOptions.find((opt) => opt.id === shippingOptionId);
            if (selectedOption) {
                if (
                    !selectedOption[OrderShippingViewDTOFields.FixedCost] &&
                    !selectedOption[OrderShippingViewDTOFields.IntegratedCost]
                ) {
                    selectedShippingCost = 0;
                } else {
                    selectedShippingCost = selectedOption[OrderShippingViewDTOFields.Cost];
                }
            }
        }

        const products: OrderProduct[] = cartItems.map((item) => ({
            [OrderProductFields.ProductId]: item.idProduct,
            // ACA TENGO QUE AGREGAR PRODUCTVARIANTID
            [OrderProductFields.ProductVariantId]: item.idProductVariant,
            [OrderProductFields.Quantity]: item.quantity,
        }));

        const data: OrderCalculationRequest = {
            [OrderCalculationRequestFields.Products]: products,
            [OrderCalculationRequestFields.CouponName]: null,
            [OrderCalculationRequestFields.ShippingOptionId]: shippingOptionId,
            [OrderCalculationRequestFields.ShippingCost]: selectedShippingCost,
        };

        try {
            const response = await HttpOrder.calculate(data);
            if (!response[BaseResponseFields.HasError]) {
                setCalculationData(response[BaseResponseFields.Data]);
            } else {
                setCalculationData(null);
            }
        } catch (error) {
            console.error("Error calculating order:", error);
            setCalculationData(null);
        } finally {
            setIsLoadingCalculation(false);
        }
    };

    // Initial calculation when drawer opens
    useEffect(() => {
        if (isOpen && cartItems.length > 0) {
            calculateOrder(selectedShippingOptionId);
        }
    }, [isOpen, cartItems]);

    const handleCalculateShipping = (e: React.FormEvent) => {
        e.preventDefault();
        if (localZipCode.trim()) {
            setContextZipCode(localZipCode);
            fetchShippingOptions(localZipCode);
        }
    };

    useEffect(() => {
        if (isOpen && contextZipCode && cartItems.length > 0 && !showShippingOptions) {
            setLocalZipCode(contextZipCode);
            fetchShippingOptions(contextZipCode);
        }
    }, [isOpen, contextZipCode]);

    const handleShippingOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value; // "optionId::branchId" o "optionId::"
        const { shippingOptionId, branchId } = parseShippingValue(value);

        const opt = getOptionById(shippingOptionId);
        const branches = (opt?.[OrderShippingViewDTOFields.Branches] ?? []) as Branch[];
        const isPickupWithBranches =
            opt?.[OrderShippingViewDTOFields.RequestAddress] === false && branches.length > 0;

        if (isPickupWithBranches && !branchId) {
            openBranchDialog(shippingOptionId);
            return;
        }

        applySelection(shippingOptionId, branchId);
    };

    const formatPrice = (price: number, fixedCost: boolean, integratedCost: boolean) => {
        if (!fixedCost && !integratedCost) {
            return " A convenir ";
        }
        return price === 0
            ? "Gratis"
            : `$${price.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const homeOptions = shippingOptions.filter(
        (option) => option[OrderShippingViewDTOFields.RequestAddress] === true,
    );
    const pickupOptions = shippingOptions.filter(
        (option) => option[OrderShippingViewDTOFields.RequestAddress] === false,
    );

    const handleViewMoreProducts = () => {
        onClose();
    };

    const handleCheckout = () => {
        onClose();
        navigate("/checkout");
    };

    const cartLineKey = (idProduct: string, idProductVariant: string) =>
        `${idProduct}-${idProductVariant}`;

    const runCartLineMutation = async (
        idProduct: string,
        idProductVariant: string,
        mutation: () => Promise<void>,
        errorMessage: string,
    ): Promise<boolean> => {
        const key = cartLineKey(idProduct, idProductVariant);
        if (pendingCartLinesRef.current.has(key)) return false;

        pendingCartLinesRef.current.add(key);
        setPendingCartLines((current) => new Set(current).add(key));
        try {
            await mutation();
            return true;
        } catch (error) {
            console.error(errorMessage, error);
            enqueueSnackbar(errorMessage, { variant: "error" });
            return false;
        } finally {
            pendingCartLinesRef.current.delete(key);
            setPendingCartLines((current) => {
                const next = new Set(current);
                next.delete(key);
                return next;
            });
        }
    };

    const handleUpdateQuantity = async (
        idProduct: string,
        idProductVariant: string,
        newQuantity: number,
    ): Promise<boolean> => {
        const validatedQuantity = Math.max(minimumPurchase, newQuantity);
        return runCartLineMutation(
            idProduct,
            idProductVariant,
            () => updateQuantity(idProduct, idProductVariant, validatedQuantity),
            "No se pudo actualizar la cantidad. Intente nuevamente.",
        );
    };

    const handleRemoveItem = async (
        idProduct: string,
        idProductVariant: string,
    ): Promise<boolean> => {
        return runCartLineMutation(
            idProduct,
            idProductVariant,
            () => removeFromCart(idProduct, idProductVariant),
            "No se pudo eliminar el producto. Intente nuevamente.",
        );
    };

    const hasItems = cartItems.length > 0;
    const hasSelectedShippingOption = !!selectedShippingOptionId;

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            ModalProps={{
                BackdropProps: {
                    sx: {
                        backgroundColor: theme.colors.overlay.dark,
                    },
                },
            }}
            sx={{
                "& .MuiDrawer-paper": {
                    width: { xs: "100%", md: "33.333333%" },
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    backgroundColor: theme.colors.background.paper,
                    color: theme.colors.text.primary,
                },
                zIndex: (theme) => theme.zIndex.drawer + 2000,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    px: STYLES.spacing.drawerPadding.xs,
                    py: 2,
                    [`@media (min-width:600px)`]: {
                        px: STYLES.spacing.drawerPadding.sm,
                    },
                }}
            >
                <DrawerHeader onClose={onClose} />

                {/* Scrollable content container with fixed height */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: "auto",
                        height: "100%",
                        mb: 2,
                    }}
                >
                    {!hasItems ? (
                        <EmptyCart />
                    ) : (
                        <>
                            {/* Cart Items */}
                            <Box sx={{ mb: 2 }}>
                                {cartItems.map((item, index) => (
                                    <ShoppingCartItem
                                        key={`${item.idProduct}-${item.idProductVariant}-${index}`}
                                        item={item}
                                        onUpdateQuantity={handleUpdateQuantity}
                                        onRemoveItem={handleRemoveItem}
                                        isPending={pendingCartLines.has(
                                            cartLineKey(item.idProduct, item.idProductVariant),
                                        )}
                                    />
                                ))}
                            </Box>

                            {/* Shipping Calculator */}
                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    sx={{
                                        mb: 1,
                                        fontFamily: "'Rubik', sans-serif",
                                        fontSize: "10px",
                                        textTransform: "uppercase",
                                        color: theme.colors.text.primary,
                                    }}
                                >
                                    MEDIOS DE ENVÍO
                                </Typography>
                                <Box
                                    component="form"
                                    onSubmit={handleCalculateShipping}
                                    sx={{ display: "flex", gap: 0, mb: 1 }}
                                >
                                    <TextField
                                        placeholder="Tu código postal"
                                        value={localZipCode}
                                        onChange={(e) => setLocalZipCode(e.target.value)}
                                        size="small"
                                        sx={{
                                            flexGrow: 1,
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 0,
                                                fontFamily: "'Rubik', sans-serif",
                                                fontSize: "12px",
                                                height: "40px",
                                                backgroundColor: theme.colors.background.paper,
                                                color: theme.colors.text.primary,
                                                "& fieldset": {
                                                    border: `1px solid ${theme.colors.greyLight}`,
                                                    borderRight: "none",
                                                },
                                                "&:hover fieldset": {
                                                    border: `1px solid ${theme.colors.greyLight}`,
                                                    borderRight: "none",
                                                },
                                                "&.Mui-focused fieldset": {
                                                    border: `1px solid ${theme.colors.greyLight}`,
                                                    borderRight: "none",
                                                },
                                            },
                                            "& .MuiOutlinedInput-input": {
                                                color: theme.colors.text.primary,
                                            },
                                            "& .MuiOutlinedInput-input::placeholder": {
                                                color: theme.colors.text.secondary,
                                                opacity: 1,
                                            },
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        variant="outlined"
                                        sx={{
                                            borderColor: theme.colors.greyLight,
                                            color: theme.colors.text.primary,
                                            backgroundColor: theme.colors.background.paper,
                                            textTransform: "uppercase",
                                            "&:hover": {
                                                borderColor: theme.colors.text.primary,
                                                backgroundColor: theme.colors.greyLight,
                                            },
                                        }}
                                    >
                                        CALCULAR
                                    </Button>
                                </Box>

                                <Typography
                                    component="a"
                                    href="https://www.correoargentino.com.ar/formularios/cpa"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        ...TYPOGRAPHY_STYLES.rubikSmall,
                                        color: STYLES.colors.secondary,
                                        textDecoration: "none",
                                        cursor: "pointer",
                                        display: "block",
                                        mb: 3,
                                    }}
                                >
                                    No sé mi código postal
                                </Typography>

                                {isLoadingShippingOptions ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    showShippingOptions && (
                                        <RadioGroup
                                            value={
                                                selectedShippingOptionId
                                                    ? makeShippingValue(
                                                          selectedShippingOptionId,
                                                          selectedShippingOptionBranchId,
                                                      )
                                                    : ""
                                            }
                                            onChange={handleShippingOptionChange}
                                        >
                                            {homeOptions.length > 0 && (
                                                <Box sx={{ mb: 3 }}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <LocalShippingOutlinedIcon
                                                            sx={{
                                                                fontSize: 18,
                                                                color: theme.colors.text.secondary,
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "'Rubik', sans-serif",
                                                                fontSize: "14px",
                                                                fontWeight: 500,
                                                                color: theme.colors.text.primary,
                                                            }}
                                                        >
                                                            Envío a domicilio
                                                        </Typography>
                                                    </Box>

                                                    {homeOptions.map((option) => (
                                                        <Box
                                                            key={option[EntityWithIdFields.Id]}
                                                            sx={{
                                                                border: `1px solid ${theme.colors.greyLight}`,
                                                                mb: 1,
                                                                p: 1,
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                value={makeShippingValue(
                                                                    option[EntityWithIdFields.Id],
                                                                    null,
                                                                )}
                                                                control={
                                                                    <Radio
                                                                        sx={{
                                                                            color: theme.colors.text
                                                                                .secondary,
                                                                            "&.Mui-checked": {
                                                                                color: theme.colors
                                                                                    .text.primary,
                                                                            },
                                                                            "& .MuiSvgIcon-root": {
                                                                                fontSize: 20,
                                                                            },
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            justifyContent:
                                                                                "space-between",
                                                                            alignItems:
                                                                                "flex-start",
                                                                            width: "100%",
                                                                            py: 1.5,
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={{ flex: 1, pr: 2 }}
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    fontFamily:
                                                                                        "'Rubik', sans-serif",
                                                                                    fontSize:
                                                                                        "13px",
                                                                                    fontWeight: 500,
                                                                                    color: theme
                                                                                        .colors.text
                                                                                        .primary,
                                                                                    mb: 0.5,
                                                                                    lineHeight: 1.4,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    option[
                                                                                        OrderShippingViewDTOFields
                                                                                            .OptionName
                                                                                    ]
                                                                                }
                                                                            </Typography>
                                                                            <Typography
                                                                                sx={{
                                                                                    fontFamily:
                                                                                        "'Rubik', sans-serif",
                                                                                    fontSize:
                                                                                        "12px",
                                                                                    color: theme
                                                                                        .colors.text
                                                                                        .secondary,
                                                                                    lineHeight: 1.4,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    option[
                                                                                        OrderShippingViewDTOFields
                                                                                            .FormattedDescription
                                                                                    ]
                                                                                }
                                                                            </Typography>
                                                                        </Box>
                                                                        <Typography
                                                                            sx={{
                                                                                fontFamily:
                                                                                    "'Rubik', sans-serif",
                                                                                fontSize: "14px",
                                                                                fontWeight: 500,
                                                                                color:
                                                                                    option[
                                                                                        OrderShippingViewDTOFields
                                                                                            .Cost
                                                                                    ] === 0
                                                                                        ? theme
                                                                                              .colors
                                                                                              .success
                                                                                        : theme
                                                                                              .colors
                                                                                              .text
                                                                                              .primary,
                                                                                whiteSpace:
                                                                                    "nowrap",
                                                                                ml: 2,
                                                                            }}
                                                                        >
                                                                            {formatPrice(
                                                                                option[
                                                                                    OrderShippingViewDTOFields
                                                                                        .Cost
                                                                                ],
                                                                                option[
                                                                                    OrderShippingViewDTOFields
                                                                                        .FixedCost
                                                                                ],
                                                                                option[
                                                                                    OrderShippingViewDTOFields
                                                                                        .IntegratedCost
                                                                                ],
                                                                            )}
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                                sx={{
                                                                    width: "100%",
                                                                    m: 0,
                                                                    alignItems: "flex-start",
                                                                    "& .MuiFormControlLabel-label":
                                                                        {
                                                                            width: "100%",
                                                                        },
                                                                }}
                                                            />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}

                                            {/* Retirar por local */}
                                            {pickupOptions.length > 0 && (
                                                <Box>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1,
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <RoomOutlinedIcon
                                                            sx={{
                                                                fontSize: 18,
                                                                color: theme.colors.text.secondary,
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "'Rubik', sans-serif",
                                                                fontSize: "14px",
                                                                fontWeight: 500,
                                                                color: theme.colors.text.primary,
                                                            }}
                                                        >
                                                            Retirar por
                                                        </Typography>
                                                    </Box>

                                                    {pickupOptions.map((option) => {
                                                        const optionId =
                                                            option[EntityWithIdFields.Id];
                                                        const branches = (option[
                                                            OrderShippingViewDTOFields.Branches
                                                        ] ?? []) as Branch[];

                                                        const isPickupWithBranches =
                                                            option[
                                                                OrderShippingViewDTOFields
                                                                    .RequestAddress
                                                            ] === false && branches.length > 0;

                                                        const isSelectedThisOption =
                                                            selectedShippingOptionId === optionId &&
                                                            !!selectedShippingOptionBranchId;

                                                        const selectedBranch = isSelectedThisOption
                                                            ? (branches.find(
                                                                  (b) =>
                                                                      (b.id ?? "") ===
                                                                      selectedShippingOptionBranchId,
                                                              ) ?? null)
                                                            : null;

                                                        const radioValue = makeShippingValue(
                                                            optionId,
                                                            isSelectedThisOption
                                                                ? selectedShippingOptionBranchId
                                                                : null,
                                                        );

                                                        if (!isPickupWithBranches) {
                                                            return (
                                                                <Box
                                                                    key={optionId}
                                                                    sx={{
                                                                        border: `1px solid ${theme.colors.greyLight}`,
                                                                        mb: 1,
                                                                        p: 1,
                                                                    }}
                                                                >
                                                                    <FormControlLabel
                                                                        value={makeShippingValue(
                                                                            optionId,
                                                                            null,
                                                                        )}
                                                                        control={
                                                                            <Radio
                                                                                sx={{
                                                                                    color: theme
                                                                                        .colors.text
                                                                                        .secondary,
                                                                                    "&.Mui-checked":
                                                                                        {
                                                                                            color: theme
                                                                                                .colors
                                                                                                .text
                                                                                                .primary,
                                                                                        },
                                                                                    "& .MuiSvgIcon-root":
                                                                                        {
                                                                                            fontSize: 20,
                                                                                        },
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Box
                                                                                sx={{
                                                                                    display: "flex",
                                                                                    justifyContent:
                                                                                        "space-between",
                                                                                    alignItems:
                                                                                        "flex-start",
                                                                                    width: "100%",
                                                                                    py: 1.5,
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        flex: 1,
                                                                                        pr: 2,
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontFamily:
                                                                                                "'Rubik', sans-serif",
                                                                                            fontSize:
                                                                                                "13px",
                                                                                            fontWeight: 500,
                                                                                            color: theme
                                                                                                .colors
                                                                                                .text
                                                                                                .primary,
                                                                                            mb: 0.5,
                                                                                            lineHeight: 1.4,
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            option[
                                                                                                OrderShippingViewDTOFields
                                                                                                    .OptionName
                                                                                            ]
                                                                                        }
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontFamily:
                                                                                                "'Rubik', sans-serif",
                                                                                            fontSize:
                                                                                                "12px",
                                                                                            color: theme
                                                                                                .colors
                                                                                                .text
                                                                                                .secondary,
                                                                                            lineHeight: 1.4,
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            option[
                                                                                                OrderShippingViewDTOFields
                                                                                                    .FormattedDescription
                                                                                            ]
                                                                                        }
                                                                                    </Typography>
                                                                                </Box>
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontFamily:
                                                                                            "'Rubik', sans-serif",
                                                                                        fontSize:
                                                                                            "14px",
                                                                                        fontWeight: 500,
                                                                                        color:
                                                                                            option[
                                                                                                OrderShippingViewDTOFields
                                                                                                    .Cost
                                                                                            ] === 0
                                                                                                ? theme
                                                                                                      .colors
                                                                                                      .success
                                                                                                : theme
                                                                                                      .colors
                                                                                                      .text
                                                                                                      .primary,
                                                                                        whiteSpace:
                                                                                            "nowrap",
                                                                                        ml: 2,
                                                                                    }}
                                                                                >
                                                                                    {formatPrice(
                                                                                        option[
                                                                                            OrderShippingViewDTOFields
                                                                                                .Cost
                                                                                        ],
                                                                                        option[
                                                                                            OrderShippingViewDTOFields
                                                                                                .FixedCost
                                                                                        ],
                                                                                        option[
                                                                                            OrderShippingViewDTOFields
                                                                                                .IntegratedCost
                                                                                        ],
                                                                                    )}
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                        sx={{
                                                                            width: "100%",
                                                                            m: 0,
                                                                            alignItems:
                                                                                "flex-start",
                                                                            "& .MuiFormControlLabel-label":
                                                                                { width: "100%" },
                                                                        }}
                                                                    />
                                                                </Box>
                                                            );
                                                        }

                                                        const providerName = option[
                                                            OrderShippingViewDTOFields.ProviderName
                                                        ] as string | undefined;
                                                        const selectedBranchMapsUrl = selectedBranch
                                                            ? mapsUrlBuilder.buildBranchUrl(
                                                                  providerName,
                                                                  selectedBranch,
                                                              )
                                                            : null;

                                                        return (
                                                            <Box
                                                                key={optionId}
                                                                sx={{
                                                                    border: `1px solid ${theme.colors.greyLight}`,
                                                                    mb: 1,
                                                                    p: 1,
                                                                }}
                                                            >
                                                                <FormControlLabel
                                                                    value={radioValue}
                                                                    control={
                                                                        <Radio
                                                                            sx={{
                                                                                color: theme.colors
                                                                                    .text.secondary,
                                                                                "&.Mui-checked": {
                                                                                    color: theme
                                                                                        .colors.text
                                                                                        .primary,
                                                                                },
                                                                                "& .MuiSvgIcon-root":
                                                                                    {
                                                                                        fontSize: 20,
                                                                                    },
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={
                                                                        <Box
                                                                            sx={{
                                                                                display: "flex",
                                                                                justifyContent:
                                                                                    "space-between",
                                                                                alignItems:
                                                                                    "flex-start",
                                                                                width: "100%",
                                                                                py: 1.5,
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    flex: 1,
                                                                                    pr: 2,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontFamily:
                                                                                            "'Rubik', sans-serif",
                                                                                        fontSize:
                                                                                            "13px",
                                                                                        fontWeight: 500,
                                                                                        color: theme
                                                                                            .colors
                                                                                            .text
                                                                                            .primary,
                                                                                        mb: 0.5,
                                                                                        lineHeight: 1.4,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        option[
                                                                                            OrderShippingViewDTOFields
                                                                                                .OptionName
                                                                                        ]
                                                                                    }
                                                                                    {selectedBranch?.nombre
                                                                                        ? ` - ${selectedBranch.nombre}`
                                                                                        : ""}
                                                                                </Typography>

                                                                                <Box
                                                                                    sx={{
                                                                                        display:
                                                                                            "flex",
                                                                                        alignItems:
                                                                                            "flex-start",
                                                                                        justifyContent:
                                                                                            "space-between",
                                                                                        gap: 1,
                                                                                        mt: 0.5,
                                                                                    }}
                                                                                >
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontFamily:
                                                                                                "'Rubik', sans-serif",
                                                                                            fontSize:
                                                                                                "12px",
                                                                                            color: theme
                                                                                                .colors
                                                                                                .text
                                                                                                .secondary,
                                                                                            lineHeight: 1.4,
                                                                                            flex: 1,
                                                                                            minWidth: 0, // clave para que corte bien en flex
                                                                                        }}
                                                                                    >
                                                                                        {selectedBranch?.direccion
                                                                                            ? selectedBranch.direccion
                                                                                            : "Seleccioná una sucursal para retiro"}
                                                                                    </Typography>

                                                                                    {selectedBranchMapsUrl && (
                                                                                        <Button
                                                                                            component="a"
                                                                                            href={
                                                                                                selectedBranchMapsUrl
                                                                                            }
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            startIcon={
                                                                                                <RoomOutlinedIcon
                                                                                                    sx={{
                                                                                                        fontSize: 16,
                                                                                                    }}
                                                                                                />
                                                                                            }
                                                                                            onMouseDown={(
                                                                                                e,
                                                                                            ) =>
                                                                                                e.stopPropagation()
                                                                                            }
                                                                                            onClick={(
                                                                                                e,
                                                                                            ) =>
                                                                                                e.stopPropagation()
                                                                                            }
                                                                                            sx={{
                                                                                                ...SHIPPING_ACTION_LINK_SX,
                                                                                                mt: 0,
                                                                                                flexShrink: 0, // evita que se achique/rompa
                                                                                            }}
                                                                                        >
                                                                                            Ver en
                                                                                            mapa
                                                                                        </Button>
                                                                                    )}
                                                                                </Box>

                                                                                <Button
                                                                                    variant="text"
                                                                                    startIcon={
                                                                                        selectedBranch ? (
                                                                                            <SwapHorizOutlinedIcon
                                                                                                sx={{
                                                                                                    fontSize: 16,
                                                                                                }}
                                                                                            />
                                                                                        ) : (
                                                                                            <StorefrontOutlinedIcon
                                                                                                sx={{
                                                                                                    fontSize: 16,
                                                                                                }}
                                                                                            />
                                                                                        )
                                                                                    }
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.preventDefault();
                                                                                        e.stopPropagation();
                                                                                        openBranchDialog(
                                                                                            optionId,
                                                                                        );
                                                                                    }}
                                                                                    sx={{
                                                                                        ...SHIPPING_ACTION_LINK_SX,
                                                                                        mt: 1,
                                                                                    }}
                                                                                >
                                                                                    {selectedBranch
                                                                                        ? "Cambiar sucursal"
                                                                                        : "Elegir sucursal"}
                                                                                </Button>
                                                                            </Box>

                                                                            <Typography
                                                                                sx={{
                                                                                    fontFamily:
                                                                                        "'Rubik', sans-serif",
                                                                                    fontSize:
                                                                                        "14px",
                                                                                    fontWeight: 500,
                                                                                    color:
                                                                                        option[
                                                                                            OrderShippingViewDTOFields
                                                                                                .Cost
                                                                                        ] === 0
                                                                                            ? theme
                                                                                                  .colors
                                                                                                  .success
                                                                                            : theme
                                                                                                  .colors
                                                                                                  .text
                                                                                                  .primary,
                                                                                    whiteSpace:
                                                                                        "nowrap",
                                                                                    ml: 2,
                                                                                }}
                                                                            >
                                                                                {formatPrice(
                                                                                    option[
                                                                                        OrderShippingViewDTOFields
                                                                                            .Cost
                                                                                    ],
                                                                                    option[
                                                                                        OrderShippingViewDTOFields
                                                                                            .FixedCost
                                                                                    ],
                                                                                    option[
                                                                                        OrderShippingViewDTOFields
                                                                                            .IntegratedCost
                                                                                    ],
                                                                                )}
                                                                            </Typography>
                                                                        </Box>
                                                                    }
                                                                    sx={{
                                                                        width: "100%",
                                                                        m: 0,
                                                                        alignItems: "flex-start",
                                                                        "& .MuiFormControlLabel-label":
                                                                            { width: "100%" },
                                                                    }}
                                                                />
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </RadioGroup>
                                    )
                                )}
                            </Box>
                        </>
                    )}
                </Box>

                {hasItems && (
                    <SummarySection
                        subtotal={subtotal}
                        shippingCost={finalShippingCost}
                        total={total}
                        productsDiscount={productsDiscount}
                        shippingDiscount={shippingDiscount}
                        shippingOriginalCost={shippingOriginalCost}
                        isShippingToBeAgreed={isShippingToBeAgreed}
                        hasSelectedShippingOption={hasSelectedShippingOption}
                        onCheckout={handleCheckout}
                        onViewMoreProducts={handleViewMoreProducts}
                    />
                )}
            </Box>

            <BranchPickerDialog
                open={branchDialogOpen}
                onClose={closeBranchDialog}
                optionName={
                    dialogOption?.[OrderShippingViewDTOFields.OptionName] as string | undefined
                }
                providerName={
                    dialogOption?.[OrderShippingViewDTOFields.ProviderName] as string | undefined
                }
                branches={dialogBranches as any}
                selectedBranchId={selectedShippingOptionBranchId}
                onSelect={(branchId) => {
                    if (!branchDialogOptionId) return;
                    applySelection(branchDialogOptionId, branchId);
                    closeBranchDialog();
                }}
                searchPlaceholder="Buscar por nombre, dirección o CP"
                dialogSx={{
                    zIndex: (theme) => theme.zIndex.drawer + 3000,
                }}
            />
        </Drawer>
    );
};

export default ShoppingCartDrawer;
